import { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseDate } from '@internationalized/date';
import { selectDateRange, selectSelectedPlant, selectOrderPhase, selectShowReasonColumn } from '@pages/reporting-pages/reduced-materials/store/selectors';
import { setDateRange, setSelectedPlant, setOrderPhase, setShowReasonColumn } from '@pages/reporting-pages/reduced-materials/store/slice';
import { useGetPlantsListQuery } from '@api/list-data/api';
import { DateRangePicker } from 'components/date-picker/DateRangePicker';
import { Select } from 'components/select/Select';
import { ComboBox } from 'components/select/ComboBox';
import { ListBoxItem } from 'react-aria-components';
import { OrderPhaseEnum } from '@api/order-reporting/types/ReducedParams';
import { ShowReasonColumn, isShowReasonColumn } from '@pages/reporting-pages/ShowReasonColumn';
import { DateRange } from '@project-types/DateRange';



const ParamsToolbar = () => {

	const dateRange = useSelector(selectDateRange);
	const selectedPlant = useSelector(selectSelectedPlant);
	const orderPhase = useSelector(selectOrderPhase);
	const showReasonColumn = useSelector(selectShowReasonColumn);

	const dispatch = useDispatch();
	const updateDateRange = (dateRange: DateRange) => dispatch(setDateRange(dateRange));
	const updateSelectedPlant = (value: string | null) => dispatch(setSelectedPlant(value));
	const updateOrderPhase = (value: string | null) => dispatch(setOrderPhase(value));
	const updateShowReasonColumn = (value: ShowReasonColumn) => dispatch(setShowReasonColumn(value));

	const { data, isLoading, isFetching, isError } = useGetPlantsListQuery({ userId: 'any' });

	return (
		<div className='reporting-params-toolbar reduced-materials-orders-toolbar'>
			
			<DateRangePicker
				label='Od - Do'
				isRequired={true}
				value={
					dateRange
					? { start: parseDate(dateRange.from), end: parseDate(dateRange.to) }
					: null
				}
				onChange={(rangeValue) => {
					if (rangeValue) {
						updateDateRange({
							from: rangeValue.start.toString(),
							to: rangeValue.end.toString(),
						});
					}
				}}
			/>

			<Select
				label='Před/Při rozvozu'
				selectedKey={orderPhase ? orderPhase : ''}
				items={[
					{ id: '', text: 'Všechny' },
					{ id: `${OrderPhaseEnum.execution}`, text: 'Při rozvozu' },
					{ id: `${OrderPhaseEnum.planning}`, text: 'Před rozvozem' }
				]}
				onSelectionChange={selected => {
					if (typeof selected === 'string') {
						updateOrderPhase(selected ? selected : null)
					}
				}}
			>
				{(item) => <ListBoxItem id={item.id}>{item.text}</ListBoxItem>}
			</Select>

			<ComboBox
				label='Depo'
				defaultItems={data
					? [
						{ id: '', text: 'Všechna depa' },
						...data.toSorted((a,b) => (a.text > b.text) ? 1 : ((b.text > a.text) ? -1 : 0)),
					]
					: []
				}
				isDisabled={isLoading || isFetching}
				errorMessage='Problém při načítání dat'
				isInvalid={isError}
				selectedKey={selectedPlant ? selectedPlant : ''}
				onSelectionChange={selected => {
					if (typeof selected === 'string') {
						updateSelectedPlant(selected);
					}
				}}
			>
				{(item) => <ListBoxItem id={item.id}>{item.text}</ListBoxItem>}
			</ComboBox>

			<Select
				label='Zobrazit sloupce s krácením'
				selectedKey={showReasonColumn}
				items={[
					{ id: `${ShowReasonColumn.planning}`, text: 'Krácení ze SAPu' },
					{ id: `${ShowReasonColumn.execution}`, text: 'Krácení z exekuce' },
					{ id: `${ShowReasonColumn.planningAndExecution}`, text: 'Krácení ze SAPu i exekuce' },
				]}
				onSelectionChange={key => {
					if (isShowReasonColumn(key)) {
						updateShowReasonColumn(key);
					}
				}}
			>
				{(item) => <ListBoxItem id={item.id}>{item.text}</ListBoxItem>}
			</Select>

		</div>
	)
};

export default memo(ParamsToolbar);